import { Link, useLocation } from 'react-router-dom';
import { AiFillLinkedin, AiOutlineClose, AiOutlineLogin, AiOutlineLogout, AiOutlineMenu, AiOutlineUser as AiUserIcon, AiOutlineUser } from 'react-icons/ai';
import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

const NavBar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { isAuthenticated, logout, user } = useAuth();

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/blog', label: 'Thoughts' },
    { path: '/professional', label: 'Day Job' },
  ] as const;

  const isActive = (path: string) => location.pathname === path;

  const handleLogout = () => {
    logout();
    setIsMobileMenuOpen(false);
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  return (
    <nav className="fixed top-0 z-20 w-full border-b border-[color:var(--wf-brass)]/30 bg-[linear-gradient(90deg,rgba(44,32,27,0.97),rgba(67,48,63,0.96))] text-[#f8f2ea] shadow-[0_12px_40px_rgba(26,18,14,0.35)] backdrop-blur-md">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
        <div className="flex-shrink-0">
          <Link
            to="/"
            className="font-serif text-xl tracking-[0.14em] text-[#f6efe5] transition-colors duration-300 hover:text-[color:var(--wf-brass-soft)] md:text-2xl"
          >
            The Wandering Fool
          </Link>
        </div>

        <div className="hidden flex-1 justify-end md:flex md:items-center md:gap-8">
          <div className="flex items-center gap-6 text-sm uppercase tracking-[0.2em] text-[#d8cdbf]">
            {navLinks.map(({ path, label }) => (
              <Link
                key={path}
                to={path}
                className={`relative pb-1 transition-colors duration-300 ${
                  isActive(path) ? 'text-[#f6efe5]' : 'hover:text-[#f2e6d6]'
                }`}
              >
                {label}
                {isActive(path) && (
                  <span className="absolute inset-x-0 -bottom-1 h-px bg-[color:var(--wf-brass)]" />
                )}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-3 border-l border-[#8f7e69]/20 pl-5 text-sm text-[#d8cdbf]">
            <a
              href="https://www.linkedin.com/in/t-adam-martin/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 transition-colors duration-300 hover:text-[#f2e6d6]"
            >
              <AiFillLinkedin className="text-base" />
              <span>LinkedIn</span>
            </a>
            {isAuthenticated ? (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsDropdownOpen((open) => !open)}
                  className="rounded-full border border-[color:var(--wf-brass)]/25 p-2 text-[#e8dccd] transition-colors duration-300 hover:border-[color:var(--wf-brass)] hover:text-[#f6efe5] focus:outline-none"
                  aria-label="User menu"
                >
                  <AiUserIcon className="text-lg" />
                </button>
                {isDropdownOpen && (
                  <div className="absolute right-0 mt-3 w-48 rounded-2xl border border-[color:var(--wf-border)] bg-[linear-gradient(180deg,rgba(251,246,238,0.98),rgba(243,233,218,0.94))] py-3 text-[color:var(--wf-ink)] shadow-[0_20px_50px_rgba(44,32,27,0.18)]">
                    {user && user.user_role === 'admin' && (
                      <>
                        <Link
                          to="/posts/new"
                          className="mx-2 block rounded-lg px-4 py-3 transition-colors duration-200 hover:bg-[rgba(195,170,122,0.12)] hover:text-[color:var(--wf-plum)]"
                          onClick={() => setIsDropdownOpen(false)}
                        >
                          Create Post
                        </Link>
                        <Link
                          to="/admin/posts"
                          className="mx-2 block rounded-lg px-4 py-3 transition-colors duration-200 hover:bg-[rgba(195,170,122,0.12)] hover:text-[color:var(--wf-plum)]"
                          onClick={() => setIsDropdownOpen(false)}
                        >
                          All Posts
                        </Link>
                        <Link
                          to="/admin/users"
                          className="mx-2 block rounded-lg px-4 py-3 transition-colors duration-200 hover:bg-[rgba(195,170,122,0.12)] hover:text-[color:var(--wf-plum)]"
                          onClick={() => setIsDropdownOpen(false)}
                        >
                          All Users
                        </Link>
                      </>
                    )}
                    <Link
                      to="/account"
                      className="mx-2 block rounded-lg px-4 py-3 transition-colors duration-200 hover:bg-[rgba(195,170,122,0.12)] hover:text-[color:var(--wf-plum)]"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      Account
                    </Link>
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsDropdownOpen(false);
                      }}
                      className="mx-2 block w-[calc(100%-1rem)] rounded-lg px-4 py-3 text-left transition-colors duration-200 hover:bg-[rgba(195,170,122,0.12)] hover:text-[color:var(--wf-plum)]"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className="inline-flex items-center gap-2 rounded-full border border-[color:var(--wf-brass)]/25 px-3 py-2 transition-colors duration-300 hover:border-[color:var(--wf-brass)] hover:text-[#f2e6d6]"
              >
                <AiOutlineLogin className="text-base" />
                <span>Login</span>
              </Link>
            )}
          </div>
        </div>

        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="rounded-full border border-[color:var(--wf-brass)]/25 p-2 text-[#e8dccd] transition-colors duration-300 hover:border-[color:var(--wf-brass)] hover:text-[#f6efe5] md:hidden"
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? <AiOutlineClose className="text-2xl" /> : <AiOutlineMenu className="text-2xl" />}
        </button>
      </div>

      <div
        className={`fixed right-0 top-[73px] h-screen w-80 transform border-l border-[color:var(--wf-border)] bg-[linear-gradient(180deg,rgba(251,246,238,0.98),rgba(243,233,218,0.96))] text-[color:var(--wf-ink)] shadow-[0_20px_50px_rgba(44,32,27,0.18)] transition-all duration-500 ease-in-out md:hidden ${
          isMobileMenuOpen ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
        }`}
      >
        <div className="flex flex-col space-y-2 p-6">
          {navLinks.map(({ path, label }) => (
            <Link
              key={path}
              to={path}
              onClick={() => setIsMobileMenuOpen(false)}
              className={`rounded-xl px-4 py-3 text-sm uppercase tracking-[0.18em] transition-all duration-200 ${
                isActive(path)
                  ? 'bg-[linear-gradient(135deg,rgba(67,48,63,1),rgba(95,70,92,1))] text-[#f6efe5] shadow-md'
                  : 'text-[#5b5048] hover:bg-[rgba(195,170,122,0.12)] hover:text-[color:var(--wf-plum)]'
              }`}
            >
              {label}
            </Link>
          ))}
          {isAuthenticated && (
            <Link
              to="/account"
              onClick={() => setIsMobileMenuOpen(false)}
            className="flex items-center gap-3 rounded-xl px-4 py-3 text-[#5b5048] transition-all duration-200 hover:bg-[rgba(195,170,122,0.12)] hover:text-[color:var(--wf-plum)]"
          >
            <AiOutlineUser className="text-xl" /> Account
          </Link>
          )}
          <a
            href="https://www.linkedin.com/in/t-adam-martin/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 rounded-xl px-4 py-3 text-[#5b5048] transition-all duration-200 hover:bg-[rgba(195,170,122,0.12)] hover:text-[color:var(--wf-plum)]"
          >
            <AiFillLinkedin className="text-xl" /> LinkedIn
          </a>
          {isAuthenticated ? (
            <button
              onClick={handleLogout}
              className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left text-[#5b5048] transition-all duration-200 hover:bg-[rgba(195,170,122,0.12)] hover:text-[color:var(--wf-plum)]"
            >
              <AiOutlineLogout className="text-xl" /> Logout
            </button>
          ) : (
            <Link
              to="/login"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-3 rounded-xl px-4 py-3 text-[#5b5048] transition-all duration-200 hover:bg-[rgba(195,170,122,0.12)] hover:text-[color:var(--wf-plum)]"
            >
              <AiOutlineLogin className="text-xl" /> Login
            </Link>
          )}
        </div>
      </div>
    </nav>
  );
};

export default NavBar;
